import { useState } from 'react';
import { Geolocation } from '@capacitor/geolocation';

interface GeolocationState {
  isDetecting: boolean;
  error: string | null;
}

export type GeoFailureReason =
  | 'permission_denied'
  | 'services_disabled'
  | 'timeout'
  | 'position_unavailable'
  | 'unknown';

interface LocationResult {
  location: string;
  latitude: number;
  longitude: number;
}

/**
 * Robust native-first geolocation flow:
 *   1. checkPermissions()
 *   2. requestPermissions() if needed
 *   3. try high-accuracy fix (GPS)
 *   4. fallback to low-accuracy (network/coarse) when high-accuracy fails
 *      (typical indoor / weak-GPS scenario)
 *
 * Returns a structured result so callers can distinguish failure modes.
 */
export interface RobustPositionResult {
  ok: boolean;
  latitude?: number;
  longitude?: number;
  reason?: GeoFailureReason;
  message?: string;
}

export async function getRobustPosition(): Promise<RobustPositionResult> {
  // 1-2. Permissions
  try {
    let perm = await Geolocation.checkPermissions();
    if (perm.location !== 'granted' && perm.coarseLocation !== 'granted') {
      perm = await Geolocation.requestPermissions({ permissions: ['location'] });
    }
    if (perm.location !== 'granted' && perm.coarseLocation !== 'granted') {
      return { ok: false, reason: 'permission_denied' };
    }
  } catch (err: any) {
    const msg = String(err?.message || err || '');
    if (/disabled|services/i.test(msg)) {
      return { ok: false, reason: 'services_disabled', message: msg };
    }
    // checkPermissions can throw on web; fall through to attempt position
  }

  // 3. High-accuracy attempt
  try {
    const pos = await Geolocation.getCurrentPosition({
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
    });
    return { ok: true, latitude: pos.coords.latitude, longitude: pos.coords.longitude };
  } catch (errHigh: any) {
    const msgHigh = String(errHigh?.message || errHigh || '');

    if (/services|disabled/i.test(msgHigh)) {
      return { ok: false, reason: 'services_disabled', message: msgHigh };
    }
    if (/denied|permission/i.test(msgHigh)) {
      return { ok: false, reason: 'permission_denied', message: msgHigh };
    }

    // 4. Low-accuracy fallback (works indoors / weak GPS)
    try {
      const pos = await Geolocation.getCurrentPosition({
        enableHighAccuracy: false,
        timeout: 15000,
        maximumAge: 60000,
      });
      return { ok: true, latitude: pos.coords.latitude, longitude: pos.coords.longitude };
    } catch (errLow: any) {
      const msgLow = String(errLow?.message || errLow || '');
      if (/timeout/i.test(msgLow)) {
        return { ok: false, reason: 'timeout', message: msgLow };
      }
      if (/unavailable|position/i.test(msgLow)) {
        return { ok: false, reason: 'position_unavailable', message: msgLow };
      }
      return { ok: false, reason: 'unknown', message: msgLow };
    }
  }
}

export const useGeolocation = () => {
  const [state, setState] = useState<GeolocationState>({
    isDetecting: false,
    error: null,
  });

  const detectLocation = async (): Promise<LocationResult | null> => {
    setState({ isDetecting: true, error: null });

    const result = await getRobustPosition();
    if (!result.ok) {
      const reason = result.reason;
      let errorMessage = 'GPS detection failed';
      switch (reason) {
        case 'permission_denied':
          errorMessage = 'Location permission denied';
          break;
        case 'services_disabled':
          errorMessage = 'Location services are disabled. Enable GPS in your device settings.';
          break;
        case 'timeout':
          errorMessage = 'GPS timeout - Make sure GPS is enabled and try outdoors';
          break;
        case 'position_unavailable':
          errorMessage = 'GPS unavailable on this device';
          break;
      }
      setState({ isDetecting: false, error: errorMessage });
      return null;
    }

    const lat = result.latitude as number;
    const lon = result.longitude as number;

    // Convert coordinates to address using Nominatim (OpenStreetMap)
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&addressdetails=1`
      );

      if (!response.ok) throw new Error('Error al obtener la dirección');

      const data = await response.json();

      let location = '';
      if (data && data.display_name) {
        const address = data.address;
        if (address) {
          const city = address.city || address.town || address.village || address.municipality;
          const stateName = address.state || address.province;
          const country = address.country;
          if (city && country) {
            location = stateName ? `${city}, ${stateName}, ${country}` : `${city}, ${country}`;
          } else {
            location = data.display_name;
          }
        } else {
          location = data.display_name;
        }
      }

      setState({ isDetecting: false, error: null });
      return { location: location || `${lat.toFixed(5)}, ${lon.toFixed(5)}`, latitude: lat, longitude: lon };
    } catch {
      // Coords are valid even if reverse geocoding fails
      setState({ isDetecting: false, error: null });
      return { location: `${lat.toFixed(5)}, ${lon.toFixed(5)}`, latitude: lat, longitude: lon };
    }
  };

  return {
    detectLocation,
    isDetecting: state.isDetecting,
    error: state.error,
  };
};
